/**
 * 知识库状态管理
 */
// TODO: [useApiQuery 迁移] 多个 CRUD 方法的 loading/try/catch/finally 模板代码可用 useApiQuery 声明式替换
import {
  exportApi,
  groupsApi,
  knowledgeBasesApi,
  type ExportResponse,
  type KnowledgeBaseImportRequest,
  type KnowledgeBaseImportResponse,
  type QAItem,
  type ResourceItem
} from '@/api/knowledgeBase'
import type {
  AddDocumentForm,
  CreateKnowledgeBaseForm,
  Document,
  DocumentQueryParams,
  HitTestResult,
  KnowledgeBase,
  KnowledgeBaseGroup,
  KnowledgeBaseQueryParams,
  Paragraph,
  StorageInfo,
  SynonymGroup
} from '@/types/knowledge-base'
import type { HitTestItem } from '@/types/knowledge-base'
import type {
  ImageSearchApiItem,
  VideoSearchApiItem,
  DocumentSearchApiItem,
  QASearchApiItem,
  QAAnswerApiItem,
  SearchApiResponse
} from '@/types/knowledge-base-api'
import { logger } from '@/utils'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// ===== 搜索结果转换函数（从 API 响应转换为前端统一类型） =====

/** 通用分数字段提取 */
function extractScores(item: ImageSearchApiItem | VideoSearchApiItem | DocumentSearchApiItem | QASearchApiItem): Pick<HitTestItem, 'semanticScore' | 'keywordScore' | 'hybridScore' | 'finalScore'> {
  return {
    semanticScore: item.semantic_score ?? null,
    keywordScore: item.keyword_score ?? null,
    hybridScore: item.hybrid_score ?? null,
    finalScore: item.final_score ?? null
  }
}

/** 转换图片搜索结果 */
function convertImageSearchResult(response: SearchApiResponse<ImageSearchApiItem>): HitTestResult {
  return {
    query: response.query,
    type: 'image',
    searchModes: response.search_modes,
    enableRerank: response.enable_rerank ?? false,
    results: response.results.map((item: ImageSearchApiItem) => ({
      rank: item.rank,
      imageId: item.image_id, resourceId: item.resource_id,
      fileName: item.file_name, imagePath: item.image_path,
      caption: item.caption, ocrText: item.ocr_text,
      // 兼容通用字段
      qaId: item.image_id, qaName: item.caption || '',
      matchedQuestionId: '', matchedQuestion: item.caption || '',
      isPrimaryQuestion: true, similarQuestions: [], answers: [],
      primaryAnswer: item.caption || '', enabled: true,
      ...extractScores(item)
    })),
    total: response.total,
    elapsedMs: response.elapsed_ms
  }
}

/** 转换视频搜索结果 */
function convertVideoSearchResult(response: SearchApiResponse<VideoSearchApiItem>): HitTestResult {
  return {
    query: response.query,
    type: 'video',
    searchModes: response.search_modes,
    enableRerank: response.enable_rerank ?? false,
    results: response.results.map((item: VideoSearchApiItem) => ({
      rank: item.rank,
      videoResultType: item.type as 'video' | 'segment',
      segmentId: item.segment_id, videoId: item.video_id,
      resourceId: item.resource_id, segmentIndex: item.segment_index,
      fileName: item.file_name, caption: item.caption,
      videoPath: item.video_path, keyFramePath: item.key_frame_path,
      keyFrameTime: item.key_frame_time,
      // 兼容通用字段
      qaId: item.segment_id || item.video_id, qaName: item.caption || '',
      matchedQuestionId: '', matchedQuestion: item.caption || '',
      isPrimaryQuestion: true, similarQuestions: [], answers: [],
      primaryAnswer: item.caption || '', enabled: true,
      ...extractScores(item)
    })),
    total: response.total,
    elapsedMs: response.elapsed_ms
  }
}

/** 转换文档搜索结果 */
function convertDocumentSearchResult(response: SearchApiResponse<DocumentSearchApiItem>): HitTestResult {
  return {
    query: response.query,
    type: 'document',
    searchModes: response.search_modes,
    enableRerank: response.enable_rerank ?? false,
    results: response.results.map((item: DocumentSearchApiItem) => ({
      rank: item.rank,
      textId: item.id, resourceId: item.resource_id,
      resultType: item.type, fileName: item.file_name,
      content: item.content, filePath: item.file_path,
      attachmentType: item.attachment_type,
      // 兼容通用字段
      qaId: item.id, qaName: item.file_name || '',
      matchedQuestionId: '', matchedQuestion: item.content?.slice(0, 100) || '',
      isPrimaryQuestion: true, similarQuestions: [],
      answers: [{ content: item.content, isPrimary: true, position: 1, images: [], videos: [], audios: [], files: [], weight: 1 }],
      primaryAnswer: item.content, enabled: true,
      ...extractScores(item)
    })),
    total: response.total,
    elapsedMs: response.elapsed_ms
  }
}

/** 转换 QA 搜索结果 */
function convertQASearchResult(response: SearchApiResponse<QASearchApiItem>): HitTestResult {
  return {
    query: response.query,
    type: response.type || 'qa',
    searchModes: response.search_modes,
    enableRerank: response.enable_rerank ?? false,
    results: response.results.map((item: QASearchApiItem) => ({
      rank: item.rank,
      qaId: item.qa_id, qaName: item.qa_name, keywords: item.keywords,
      matchedQuestionId: item.matched_question_id,
      matchedQuestion: item.matched_question,
      isPrimaryQuestion: item.is_primary_question,
      similarQuestions: item.similar_questions,
      answers: (item.answers || []).map((ans: QAAnswerApiItem) => ({
        content: ans.content, isPrimary: ans.is_primary, position: ans.position,
        images: ans.images, videos: ans.videos, audios: ans.audios,
        files: ans.files, weight: ans.weight
      })),
      primaryAnswer: item.primary_answer, enabled: item.enabled,
      semanticScore: item.semantic_score ?? null, keywordScore: item.keyword_score ?? null,
      hybridScore: item.hybrid_score ?? null, finalScore: item.final_score ?? null
    })),
    total: response.total,
    elapsedMs: response.elapsed_ms,
    searchedAt: response.searched_at
  }
}

export const useKnowledgeBaseStore = defineStore('knowledgeBase', () => {
  // ===== 状态 =====

  // 知识库列表
  const knowledgeBases = ref<KnowledgeBase[]>([])

  // 当前选中的知识库
  const currentKnowledgeBase = ref<KnowledgeBase | null>(null)

  // 文档列表
  const documents = ref<Document[]>([])

  // QA 问答列表
  const qaList = ref<QAItem[]>([])
  const qaTotal = ref(0)
  const qaLoading = ref(false)

  // 资源列表（knowledge_resources 表）
  const resourceList = ref<ResourceItem[]>([])
  const resourceTotal = ref(0)
  const resourceLoading = ref(false)

  // 当前选中的文档
  const currentDocument = ref<Document | null>(null)

  // 段落列表
  const paragraphs = ref<Paragraph[]>([])

  // 近义词库
  const synonymGroups = ref<SynonymGroup[]>([])

  // 命中测试结果
  const hitTestResults = ref<HitTestResult | null>(null)

  // 知识库分组
  const groups = ref<KnowledgeBaseGroup[]>([])

  // 存储容量信息（初始为空，应由 API 加载）
  const storageInfo = ref<StorageInfo>({
    used: 0,
    total: 0,
    usedFormatted: '-',
    totalFormatted: '-'
  })

  // 加载状态
  const loading = ref(false)
  const documentsLoading = ref(false)
  const paragraphsLoading = ref(false)

  // 弹窗状态
  const createModalVisible = ref(false)
  const addDocumentModalVisible = ref(false)
  const editParagraphModalVisible = ref(false)

  // 查询参数
  const queryParams = ref<KnowledgeBaseQueryParams>({
    page: 1,
    pageSize: 20
  })

  const documentQueryParams = ref<DocumentQueryParams>({
    page: 1,
    pageSize: 20
  })

  // 总数
  const total = ref(0)
  const documentTotal = ref(0)

  // ===== 计算属性 =====

  // 剩余容量
  const remainingStorage = computed(() => {
    return storageInfo.value.total - storageInfo.value.used
  })

  // 容量使用百分比（守卫除零 NaN）
  const storageUsagePercent = computed(() => {
    if (storageInfo.value.total === 0) return 0
    return Math.round((storageInfo.value.used / storageInfo.value.total) * 100)
  })

  // 按类型分组的知识库数量
  const knowledgeBaseCountByType = computed(() => {
    const result = {
      document: 0,
      multimodal: 0,
      qa: 0
    }
    knowledgeBases.value.forEach(kb => {
      result[kb.type]++
    })
    return result
  })

  // ===== 方法 =====

  // 加载知识库列表
  async function loadKnowledgeBases(params?: Partial<KnowledgeBaseQueryParams>) {
    loading.value = true
    try {
      // 合并查询参数
      if (params) {
        queryParams.value = { ...queryParams.value, ...params }
      }

      // 调用后端 API（project_id 由拦截器通过 X-Project-Id header 自动注入）
      const result = await knowledgeBasesApi.list({
        keyword: queryParams.value.keyword,
        type: queryParams.value.type,
        status: queryParams.value.status
      })

      knowledgeBases.value = result.knowledgeBases
      groups.value = result.groups
      total.value = knowledgeBases.value.length
      updateGroupCounts()
    } catch (error) {
      logger.error('加载知识库列表失败:', error)
      // 清空数据，抛出错误让调用方处理
      knowledgeBases.value = []
      groups.value = []
      total.value = 0
      throw error
    } finally {
      loading.value = false
    }
  }

  // 加载知识库详情
  async function loadKnowledgeBaseDetail(id: string) {
    loading.value = true
    try {
      // 先从本地缓存查找
      const kb = knowledgeBases.value.find(k => k.id === id)
      if (kb) {
        currentKnowledgeBase.value = kb
        return
      }

      // 从后端 API 获取
      const detail = await knowledgeBasesApi.getById(id)
      currentKnowledgeBase.value = detail
    } catch (error) {
      logger.error('加载知识库详情失败:', error)
      currentKnowledgeBase.value = null
      throw error
    } finally {
      loading.value = false
    }
  }

  // 创建知识库
  async function createKnowledgeBase(form: CreateKnowledgeBaseForm & { groupId?: string }) {
    loading.value = true
    try {
      // 调用后端 API
      const newKb = await knowledgeBasesApi.create(form)
      knowledgeBases.value.unshift(newKb)
      updateGroupCounts()
      return newKb
    } catch (error) {
      logger.error('创建知识库失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 更新知识库
  async function updateKnowledgeBase(id: string, data: Partial<KnowledgeBase>) {
    loading.value = true
    try {
      // 调用后端 API 更新
      const updatedKb = await knowledgeBasesApi.update(id, {
        name: data.name,
        description: data.description,
        status: data.status,
        group_id: data.groupId === undefined ? undefined : (data.groupId || null),
        embedding_model_id: data.embeddingModelId,
        rerank_model_id: data.rerankModelId,
        indexing_technique: data.indexingTechnique,
        retrieval_model: data.retrievalModel
      })

      // 更新本地缓存
      const index = knowledgeBases.value.findIndex(k => k.id === id)
      if (index !== -1) {
        knowledgeBases.value[index] = updatedKb
      }

      if (currentKnowledgeBase.value?.id === id) {
        currentKnowledgeBase.value = updatedKb
      }

      updateGroupCounts()
      return updatedKb
    } catch (error) {
      logger.error('更新知识库失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 删除知识库
  async function deleteKnowledgeBase(id: string) {
    loading.value = true
    try {
      // 调用后端 API
      await knowledgeBasesApi.delete(id)

      const index = knowledgeBases.value.findIndex(k => k.id === id)
      if (index !== -1) {
        knowledgeBases.value.splice(index, 1)
      }

      if (currentKnowledgeBase.value?.id === id) {
        currentKnowledgeBase.value = null
      }
    } catch (error) {
      logger.error('删除知识库失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 加载文档列表
  async function loadDocuments(_knowledgeBaseId: string, params?: Partial<DocumentQueryParams>) {
    documentsLoading.value = true
    try {
      if (params) {
        documentQueryParams.value = { ...documentQueryParams.value, ...params }
      }

      // TODO: 调用后端 API 获取文档列表
      // const result = await documentsApi.list(knowledgeBaseId, documentQueryParams.value)
      // documents.value = result.documents
      // documentTotal.value = result.total

      // 暂时返回空列表，等后端 API 实现
      documents.value = []
      documentTotal.value = 0
    } catch (error) {
      logger.error('加载文档列表失败:', error)
      documents.value = []
      documentTotal.value = 0
      throw error
    } finally {
      documentsLoading.value = false
    }
  }

  // 加载 QA 问答列表
  async function loadQAList(knowledgeBaseId: string, params?: { page?: number; pageSize?: number; keyword?: string }) {
    qaLoading.value = true
    try {
      const result = await knowledgeBasesApi.getQAList(knowledgeBaseId, {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        keyword: params?.keyword,
        detail: true
      })
      qaList.value = result.items || []
      qaTotal.value = result.total || 0
      logger.info(`加载 QA 列表成功，共 ${qaTotal.value} 条`)
    } catch (error) {
      logger.error('加载 QA 列表失败:', error)
      qaList.value = []
      qaTotal.value = 0
      throw error
    } finally {
      qaLoading.value = false
    }
  }

  // 加载资源列表（knowledge_resources 表）
  // silent: 静默刷新，不显示 loading 状态（用于自动刷新）
  async function loadResourceList(knowledgeBaseId: string, params?: { page?: number; pageSize?: number; keyword?: string; processingStatus?: string; silent?: boolean }) {
    if (!params?.silent) {
      resourceLoading.value = true
    }
    try {
      const result = await knowledgeBasesApi.getResourceList(knowledgeBaseId, {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        keyword: params?.keyword,
        processingStatus: params?.processingStatus
      })
      resourceList.value = result.items || []
      resourceTotal.value = result.total || 0
      logger.info(`加载资源列表成功，共 ${resourceTotal.value} 条`)
    } catch (error) {
      logger.error('加载资源列表失败:', error)
      resourceList.value = []
      resourceTotal.value = 0
      throw error
    } finally {
      if (!params?.silent) {
        resourceLoading.value = false
      }
    }
  }

  // 添加文档
  async function addDocuments(
    knowledgeBaseId: string,
    form: AddDocumentForm,
    knowledgeBaseInfo?: {
      name: string
      type: number
      description?: string
      groupId?: string
      groupName?: string
      embeddingModelId?: string
    }
  ) {
    documentsLoading.value = true
    try {
      // 1. 先将文件上传到 OSS
      const uploadedFiles: { file_url: string; file_name: string; file_size: number }[] = []

      if (form.files && form.files.length > 0) {
        for (const file of form.files) {
          const result = await knowledgeBasesApi.uploadFile(knowledgeBaseId, file)
          uploadedFiles.push({
            file_url: result.oss_url,
            file_name: result.filename,
            file_size: result.size
          })
        }
      } else if (form.ossFiles && form.ossFiles.length > 0) {
        // 使用包含大小信息的文件列表（新方式）
        form.ossFiles.forEach((file) => {
          uploadedFiles.push({
            file_url: file.url,
            file_name: file.name,
            file_size: file.size
          })
        })
      } else if (form.ossUrls && form.ossUrls.length > 0) {
        // 兼容旧方式：如果已有 OSS URL，直接使用（无大小信息）
        form.ossUrls.forEach((url) => {
          // 从 URL 中提取文件名
          const urlPath = new URL(url).pathname
          const fileName = decodeURIComponent(urlPath.split('/').pop() || 'unknown')
          uploadedFiles.push({
            file_url: url,
            file_name: fileName,
            file_size: 0
          })
        })
      }

      if (uploadedFiles.length === 0) {
        throw new Error('没有可上传的文件')
      }

      // 2. 调用后端 API 进行处理
      const kb = currentKnowledgeBase.value
      const kbInfo = knowledgeBaseInfo || {
        name: kb?.name || '',
        type: kb?.type === 'document' ? 1 : kb?.type === 'multimodal' ? 2 : kb?.type === 'qa' ? 3 : 1,
        description: kb?.description,
        groupId: kb?.groupId,
        embeddingModelId: kb?.embeddingModelId
      }

      const result = await knowledgeBasesApi.uploadDocuments(
        knowledgeBaseId,
        {
          id: knowledgeBaseId,
          name: kbInfo.name,
          type: kbInfo.type,
          description: kbInfo.description,
          group_id: kbInfo.groupId,
          group_name: kbInfo.groupName,
          embedding_model_id: kbInfo.embeddingModelId
        },
        uploadedFiles,
        {
          mode: form.parsingConfig?.segmentationType === 'custom' ? 'custom' : 'automatic',
          segmentation: {
            method: form.parsingConfig?.segmentationType === 'smart' ? 'heading_paragraph' : undefined,
            separator: form.parsingConfig?.customSeparator,
            max_tokens: form.parsingConfig?.customMaxTokens ?? form.parsingConfig?.customChunkSize ?? 10000,
            chunk_overlap: form.parsingConfig?.customChunkOverlap ?? 0
          }
        },
        {
          // 默认提取并处理文档中的图片
          generateCaption: form.parsingConfig?.generateCaption ?? true,
          category: form.category
        }
      )

      logger.info('文档上传成功:', result)

      // 3. 刷新资源列表
      await loadResourceList(knowledgeBaseId)

      return result
    } catch (error) {
      logger.error('添加文档失败:', error)
      throw error
    } finally {
      documentsLoading.value = false
    }
  }

  // 删除文档
  async function deleteDocument(documentId: string) {
    documentsLoading.value = true
    try {
      // TODO: 当后端文档 API 实现后，替换为真实调用
      // await documentsApi.delete(documentId)

      const index = documents.value.findIndex(d => d.id === documentId)
      if (index !== -1) {
        documents.value.splice(index, 1)
        documentTotal.value = documents.value.length

        // 更新知识库文档数量
        if (currentKnowledgeBase.value) {
          currentKnowledgeBase.value.documentCount--
        }
      }
    } catch (error) {
      logger.error('删除文档失败:', error)
      throw error
    } finally {
      documentsLoading.value = false
    }
  }

  // 切换文档启用状态
  async function toggleDocumentEnabled(documentId: string, enabled: boolean) {
    const doc = documents.value.find(d => d.id === documentId)
    if (doc) {
      doc.enabled = enabled
    }
  }

  // 加载段落列表
  async function loadParagraphs(_documentId: string) {
    paragraphsLoading.value = true
    try {
      // TODO: 调用后端 API 获取段落列表
      // const result = await paragraphsApi.list(documentId)
      // paragraphs.value = result

      // 暂时返回空列表，等后端 API 实现
      paragraphs.value = []
    } catch (error) {
      logger.error('加载段落列表失败:', error)
      paragraphs.value = []
      throw error
    } finally {
      paragraphsLoading.value = false
    }
  }

  // 更新段落
  async function updateParagraph(paragraphId: string, data: Partial<Paragraph>) {
    const index = paragraphs.value.findIndex(p => p.id === paragraphId)
    if (index !== -1) {
      paragraphs.value[index] = {
        ...paragraphs.value[index],
        ...data
      }
    }
  }

  // 删除段落
  async function deleteParagraph(paragraphId: string) {
    const index = paragraphs.value.findIndex(p => p.id === paragraphId)
    if (index !== -1) {
      paragraphs.value.splice(index, 1)
    }
  }

  // 命中测试
  async function runHitTest(
    knowledgeBaseId: string,
    query: string,
    options?: {
      searchModes?: ('semantic' | 'keyword' | 'hybrid')[]
      // Dify 风格新字段（first-class，与后端对齐）
      retrievalMethod?:
        | 'semantic_search'
        | 'keyword_search'
        | 'full_text_search'
        | 'hybrid_search'
      rerankingMode?: 'reranking_model' | 'weighted_score'
      weights?: { vector_weight: number; keyword_weight: number }
      enableRerank?: boolean
      scoreThreshold?: number
      // Dify ``score_threshold_enabled`` 三态：未传 → 由后端 resolver 走 KB
      // 默认；显式 false → 即使传了 scoreThreshold 也不过滤
      scoreThresholdEnabled?: boolean
      topK?: number
      resourceIds?: string[]
      enabledOnly?: boolean
      kbType?: 'qa' | 'document' | 'image' | 'video'
    }
  ) {
    loading.value = true
    try {
      const kbType = options?.kbType || 'qa'
      const searchOpts = {
        searchModes: options?.searchModes,
        retrievalMethod: options?.retrievalMethod,
        rerankingMode: options?.rerankingMode,
        weights: options?.weights,
        enableRerank: options?.enableRerank,
        scoreThreshold: options?.scoreThreshold,
        scoreThresholdEnabled: options?.scoreThresholdEnabled,
        topK: options?.topK
      }

      // 根据类型调用对应 API 并转换结果
      switch (kbType) {
        case 'image':
          hitTestResults.value = convertImageSearchResult(
            await knowledgeBasesApi.searchImages(knowledgeBaseId, query, searchOpts)
          )
          break
        case 'video':
          hitTestResults.value = convertVideoSearchResult(
            await knowledgeBasesApi.searchVideos(knowledgeBaseId, query, searchOpts)
          )
          break
        case 'document':
          hitTestResults.value = convertDocumentSearchResult(
            await knowledgeBasesApi.searchDocuments(knowledgeBaseId, query, {
              ...searchOpts, resourceIds: options?.resourceIds
            })
          )
          break
        default:
          hitTestResults.value = convertQASearchResult(
            await knowledgeBasesApi.searchQA(knowledgeBaseId, query, options)
          )
      }

      return hitTestResults.value
    } catch (error) {
      logger.error('命中测试失败:', error)
      hitTestResults.value = null
      throw error
    } finally {
      loading.value = false
    }
  }

  // 加载近义词库
  async function loadSynonymGroups(_knowledgeBaseId: string) {
    loading.value = true
    try {
      // TODO: 调用后端 API 获取近义词库
      // const result = await synonymsApi.list(knowledgeBaseId)
      // synonymGroups.value = result

      // 暂时返回空列表，等后端 API 实现
      synonymGroups.value = []
    } catch (error) {
      logger.error('加载近义词库失败:', error)
      synonymGroups.value = []
      throw error
    } finally {
      loading.value = false
    }
  }

  // 添加近义词组
  async function addSynonymGroup(knowledgeBaseId: string, mainWord: string, synonyms: string[]) {
    const newGroup: SynonymGroup = {
      id: `syn-${Date.now()}`,
      knowledgeBaseId,
      mainWord,
      synonyms,
      enabled: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    synonymGroups.value.push(newGroup)
    return newGroup
  }

  // 删除近义词组
  async function deleteSynonymGroup(groupId: string) {
    const index = synonymGroups.value.findIndex(g => g.id === groupId)
    if (index !== -1) {
      synonymGroups.value.splice(index, 1)
    }
  }

  // 打开创建知识库弹窗
  function openCreateModal() {
    createModalVisible.value = true
  }

  // 关闭创建知识库弹窗
  function closeCreateModal() {
    createModalVisible.value = false
  }

  // 打开添加文档弹窗
  function openAddDocumentModal() {
    addDocumentModalVisible.value = true
  }

  // 关闭添加文档弹窗
  function closeAddDocumentModal() {
    addDocumentModalVisible.value = false
  }

  // 创建分组
  async function createGroup(data: { name: string; description?: string }) {
    loading.value = true
    try {
      // 检查分组名称是否重复
      const trimmedName = data.name.trim()
      const existingGroup = groups.value.find(
        g => g.name.trim().toLowerCase() === trimmedName.toLowerCase()
      )
      if (existingGroup) {
        throw new Error('分组名称已存在，请使用其他名称')
      }

      // 调用后端 API
      const newGroup = await groupsApi.create({
        name: trimmedName,
        description: data.description?.trim()
      })

      // 按创建时间倒序插入（新分组在顶部）
      groups.value.unshift(newGroup)
      updateGroupCounts()
      return newGroup
    } catch (error) {
      logger.error('创建分组失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 更新分组
  async function updateGroup(id: string, data: Partial<KnowledgeBaseGroup>) {
    loading.value = true
    try {
      const updatedGroup = await groupsApi.update(id, {
        name: data.name,
        description: data.description
      })

      const index = groups.value.findIndex(g => g.id === id)
      if (index !== -1) {
        groups.value[index] = updatedGroup
        updateGroupCounts()
      }
      return updatedGroup
    } catch (error) {
      logger.error('更新分组失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 删除分组
  async function deleteGroup(id: string) {
    loading.value = true
    try {
      // 调用后端 API
      await groupsApi.delete(id)

      const index = groups.value.findIndex(g => g.id === id)
      if (index !== -1) {
        groups.value.splice(index, 1)
        updateGroupCounts()
      }
    } catch (error) {
      logger.error('删除分组失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 更新分组的知识库数量统计
  function updateGroupCounts() {
    groups.value.forEach(group => {
      group.knowledgeBaseCount = knowledgeBases.value.filter(
        kb => kb.groupId === group.id
      ).length
    })
  }

  // 导出知识库（服务端生成完整单库导出包）
  async function exportKnowledgeBase(id: string, format: 'json' | 'excel' = 'json'): Promise<ExportResponse> {
    loading.value = true
    try {
      return await exportApi.exportKnowledgeBase(id, { format })
    } finally {
      loading.value = false
    }
  }

  // 导入知识库
  async function importKnowledgeBase(data: KnowledgeBaseImportRequest): Promise<KnowledgeBaseImportResponse> {
    loading.value = true
    try {
      const result = await exportApi.importKnowledgeBase(data)
      await loadKnowledgeBases()
      return result
    } finally {
      loading.value = false
    }
  }

  // 重置状态
  function reset() {
    currentKnowledgeBase.value = null
    documents.value = []
    currentDocument.value = null
    paragraphs.value = []
    hitTestResults.value = null
  }

  return {
    // 状态
    knowledgeBases,
    currentKnowledgeBase,
    documents,
    currentDocument,
    paragraphs,
    synonymGroups,
    hitTestResults,
    groups,
    storageInfo,
    loading,
    documentsLoading,
    paragraphsLoading,
    createModalVisible,
    addDocumentModalVisible,
    editParagraphModalVisible,
    queryParams,
    documentQueryParams,
    total,
    documentTotal,
    // QA 相关状态
    qaList,
    qaTotal,
    qaLoading,
    // 资源相关状态
    resourceList,
    resourceTotal,
    resourceLoading,

    // 计算属性
    remainingStorage,
    storageUsagePercent,
    knowledgeBaseCountByType,

    // 方法
    loadKnowledgeBases,
    loadKnowledgeBaseDetail,
    createKnowledgeBase,
    updateKnowledgeBase,
    deleteKnowledgeBase,
    loadDocuments,
    loadQAList,
    loadResourceList,
    addDocuments,
    deleteDocument,
    toggleDocumentEnabled,
    loadParagraphs,
    updateParagraph,
    deleteParagraph,
    runHitTest,
    loadSynonymGroups,
    addSynonymGroup,
    deleteSynonymGroup,
    openCreateModal,
    closeCreateModal,
    openAddDocumentModal,
    closeAddDocumentModal,
    createGroup,
    updateGroup,
    deleteGroup,
    exportKnowledgeBase,
    importKnowledgeBase,
    reset
  }
})

